import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/siteDefaults";

const ContentContext = createContext(null);

function isObj(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

// Deep-merge: objects merge recursively; arrays & primitives from source override.
export function deepMerge(base, src) {
  if (src === undefined || src === null) return base;
  if (Array.isArray(base) || Array.isArray(src)) return src ?? base;
  if (isObj(base) && isObj(src)) {
    const out = { ...base };
    for (const k of Object.keys(src)) out[k] = deepMerge(base[k], src[k]);
    return out;
  }
  return src;
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const preview = params.get("preview") === "1" && !!localStorage.getItem("prc_token");
    const endpoint = preview ? "/content/draft" : "/content";
    return api
      .get(endpoint)
      .then((res) => setContent(deepMerge(DEFAULT_CONTENT, res.data || {})))
      .catch(() => setContent(DEFAULT_CONTENT))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ContentContext.Provider value={{ content, loaded, reload: load }}>
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext).content;
export const useContentCtx = () => useContext(ContentContext);
